// The chain runner (issue 1499; docs/MRCS/workbench.md §5, §7; step 4 of §11): a rule block walked at the table. Pure:
// no I/O, no clock, no randomness of its own. Everything it reads arrives through the `ChainContext`, every die it
// throws comes from the context's `rng`, and every person's answer comes back through `resumeChain`, so a run is the
// same run every time it is replayed with the same answers.
//
// THE BLOCK is the document `scripts/mrcs/blocks.js` validates (read its header): nodes that are a `use` of another
// block, a `formula` of the sheet engine's operations, a `direct` (one value from a source) or an `end`. The runner
// trusts a block that validated; what it cannot do with one it is handed it says in words and stops (`stopped`), never
// throws.
//
// WHAT RUNS. A block's chains start at its finishing nodes — its Ends and its `use`s of mechanics — that no End's
// `then` goes on to, in the order the nodes are listed. Every other node is a value, worked out when something reads it
// (`in`, an End's `value`/`against`/`resolves.value`, a `use`'s `inputs`) and kept for the rest of that block's run.
// A formula is evaluated by the sheet engine's own evaluator (`generated/sheetEvaluator.ts`, the server's copied
// verbatim by `npm run mrcs:client-sheet`; workbench §7): the runner writes each `in` and Direct into the tree as the
// literal it gave (a number, a yes or no, dice as `sum` of `dice` pieces and the bonus) and hands the tree over. An
// operation's result with a fraction left (a number as it stands, a grip's 1½, is never rounded) is rounded by the system's default (`rounding`, §3.4: `features.rounding.default`); with none,
// it stays as it is. Dice are never thrown inside a formula: they reach an End, which throws them.
//
// ROLES (§4.2). `runChain` is given the creatures that fill the block's roles, by role key. A role picked `each` takes a
// list and the whole chain runs once per creature in it (one run per combination when there are several), each with its
// own repeat count; every other role takes one creature. A `use` maps the inner block's roles onto this one's by its
// `roles` (the same key by default), and a `then` that goes on to a library block passes the roles on by the same key.
//
// THE DIRECT CONTRACT (`ChainContext.direct`). The runner never reads a sheet, a stat block or the board itself: for
// each Direct of source `sheet`, `stat`, `defence` or `board` it asks the context, naming the creature the Direct's role
// is filled by, and uses the answer as it is:
//   sheet    { purpose, which?, creature } → a number, a yes or no, or dice ({ dice: [{ count, sides }], bonus }).
//            `which` (left open by issue 1496, settled here) is passed through untouched: it is the context's job to
//            find, among the creature's sheet fields carrying `purpose`, the one `which` names — `ability_score` +
//            `strength` is the Strength score, `saving_throw` + `reflex` the Reflex save total. The table's context
//            matches `which` against the field's key and label words (and, for a save, the system's
//            `features.combat.saves` key), the same way it finds a defence today; the runner holds no field keys.
//   stat     { register, creature } → the number the creature's stat block holds in that register.
//   defence  { key, creature } → the creature's defence, as the table reads `features.combat.defences` today.
//   board    { measure, creature, to? } → the board's measure; `to` is the creature the second role is filled by.
//   condition { key, creature } → yes or no: the creature carries the condition, effect or feat `key` (part 2).
// `null` is "not known": the run stops and says which value it is missing — unless the context says `askUnknown` (a
// trigger's chain, qa-runs 264): then a number not known (any Direct but a condition's or an attack's yes or no) pauses
// the run with a DM-only question naming it ("Bearer's caster level?", `ask.value`, no outcomes), and the DM's answer, a
// whole number, is recorded in `state.choices` in the order it was asked, so a resume or a replay reads it again.
// `dice` and `fixed` Directs need no context. A `fixed` Direct naming an `input` takes the value the using block
// passed in (`use` … `inputs`, blocks.js INPUTS), or `runChain`'s own `inputs`; with none, its own value.
//
// ENDS (§5.1). An End with branches is settled by the rules through its `judge`, or by a person: `resolved_by` `actor`
// or `dm` pauses the run (`paused`, with who is asked and the outcomes as choices) and `resumeChain` carries on with the
// chosen outcome. The judges read the system's numbers from `features.combat` (`ChainContext.combat`, the table's
// parsed `CombatRules`), as `attack.ts` and `save.ts` do:
//   attack   the value's dice thrown: a natural result the system names decides (3.5: 20 hits, 1 misses), else the
//            total against `against`. A hit whose natural roll reaches the threat range is a threat; the system's
//            confirmation (`roll_again`) throws the same dice again with the same bonus, and a confirmed threat is a
//            critical (`none`: every threat is). The threat range and multiplier are the attack's own when the context
//            names them (`critical`, from the weapon), else the system's. The outcome is `critical` when the End lists
//            that branch and the threat was confirmed; otherwise `threat` for any threat that hit, when it lists that
//            (the shipped Attack roll does: its End's `judged.critical` says whether it was confirmed); else `hit`.
//   save     the value's dice thrown: a natural result the system names decides (3.5: 20 succeeds, 1 fails), else
//            the total at or above `against` succeeds: `success` or `failure`.
//   compare  the value (thrown, if dice) at or above `against`: the End's first branch, else its second.
//   damage   the value's dice thrown, the bonus added, then the End's `extra` (issue 1502: dice a critical never
//            multiplies, a flaming weapon's fire or sneak attack) thrown once and added, never less than the system's
//            least damage, dealt as its kind (`damage.minimum.kind`: Pathfinder's 1 nonlethal). After a critical
//            earlier on the same chain (an attack End's `then`), the value's dice are thrown the multiplier's number of
//            times and its bonus multiplied, as `attack.ts`'s `damageOf` does; `extra` never is.
// An End then resolves (`resolves`: damage, an effect or a condition onto a role's creature, or a number to a sheet
// purpose) and chains on (`then`: a node of the block, or a library block `{ block, version }`). Resolving is reported,
// never done: the result lists each End's outcome and what it resolves, for the table to commit.
//
// THE REPEAT CEILING (§5.3): a run may run one block 20 more times after its first — 20 repeats — and on the 21st repeat
// it stops with the words "<entry> stopped after 20 repeats of <block>". A mechanic, an end or an entry counts each time
// its chain is entered (by a `use` or a `then`), and an End each time a `then` reaches it again; a primitive's value is
// worked out, not run, and does not count.
//
// RESUMING. A paused run's `state` holds every face thrown and every answer given so far. `resumeChain` runs the block
// again from the start with that state: the recorded faces are used before the rng is asked, the recorded answers
// before anyone is asked, so the run reaches the same pause and goes past it. The context must answer the same as it
// did (part 2 commits the facts a pause was reached with).
import { modTargetWords } from './modifiers.js';
import { isAdvantageWord } from './advantage.js';
import { evaluateRule } from '../generated/sheetEvaluator.js';
import { leastDamage } from './attack.js';
/** The most times one block may repeat in one run of a chain (§5.3). */
export const CHAIN_REPEATS = 20;
/** The outcome of a creature choice's No button (issue 1509): never a creature's id. */
export const NONE_CHOSEN = 'none';
/** The most buttons a number choice shows (feats.md §6.1). */
export const MAX_CHOICES = 21;
/** The most an answered number may be (qa-runs 264): the bound a token's number has. */
export const CHAIN_NUMBER_MAX = 1_000_000;
/** A DM's answer to a number asked (qa-runs 264): a whole number within the bound, as text; else null. */
export function chainNumber(text) {
    if (!/^\s*[+-]?\d{1,7}\s*$/.test(text))
        return null;
    const n = Number(text.trim());
    return Number.isInteger(n) && Math.abs(n) <= CHAIN_NUMBER_MAX ? n : null;
}
class Pause {
    ask;
    constructor(ask) {
        this.ask = ask;
    }
}
class Stop {
    reason;
    constructor(reason) {
        this.reason = reason;
    }
}
/** A Direct the context does not know (part 3: the DM is asked for the piece it was in). */
class Unknown extends Stop {
}
/** A Direct's value in words, for the DM's question (qa-runs 264): "Strength", "caster level", "normal defence". */
function valueWords(d) {
    const words = (t) => (t ?? '').replace(/_/g, ' ').trim();
    if (d.source === 'sheet')
        return d.which ? ((w) => `${w.charAt(0).toUpperCase()}${w.slice(1)}`)(words(d.which)) : words(d.purpose) || 'number';
    if (d.source === 'stat')
        return words(d.register) || 'number';
    if (d.source === 'defence')
        return `${words(d.key)} defence`.trim();
    return words(d.measure) || 'number';
}
const evaluate = evaluateRule;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isDice = (v) => isObj(v) && Array.isArray(v.dice) && typeof v.bonus === 'number';
const MAX_FRAMES = 64;
class Run {
    top;
    ctx;
    state;
    faces = [];
    ends = [];
    counts = new Map();
    used = 0;
    /** The frame the End being run stands in. */
    frameNow = null;
    constructor(top, ctx, state) {
        this.top = top;
        this.ctx = ctx;
        this.state = state;
    }
    draw(sides) {
        const i = this.faces.length;
        const f = i < this.state.faces.length ? this.state.faces[i] : this.ctx.rng(sides);
        if (!Number.isInteger(f) || f < 1 || f > sides)
            throw new Stop(`a d${sides} came up ${f}, which is not one of its faces`);
        this.faces.push(f);
        return f;
    }
    /** One creature's run done: its saves told where it stopped, and the runs a rethrow kept back on the tape after it. */
    finish(index, firstEnd) {
        for (const e of this.ends.slice(firstEnd))
            if (e.judged?.at)
                e.judged.at.run = { index, faces: this.faces.length, answers: this.used };
        const rest = this.state.rest;
        if (rest?.run === index)
            this.state = { faces: [...this.faces, ...rest.faces], choices: [...this.state.choices.slice(0, this.used), ...rest.choices] };
    }
    choose(ask) {
        if (this.used < this.state.choices.length) {
            const c = this.state.choices[this.used++];
            if (!ask.outcomes.includes(c))
                throw new Stop(`the answer "${c}" is not one of ${ask.blockName}'s outcomes`);
            return c;
        }
        throw new Pause(ask);
    }
    /** A role a person fills (`ChainRole.chooses`): one button a creature the context lists, with its own question. None to choose stops it, in words. */
    chooseRole(block, r, roles) {
        const c = r.chooses;
        const self = block.roles.find((x) => x.pick === 'self')?.key;
        const of = self === undefined ? undefined : roles[self];
        const next = c.adjacent_to === undefined ? undefined : roles[c.adjacent_to];
        const listed = of !== undefined && this.ctx.creatures ? this.ctx.creatures({ of, relation: c.relation, ...(next !== undefined ? { adjacentTo: next } : {}) }) : [];
        const list = (listed ?? []).slice(0, MAX_CHOICES);
        if (!list.length)
            throw new Stop(`${block.name}: there is no creature to choose`);
        return this.choose({
            who: 'actor', block: block.id, blockName: block.name, node: r.key, outcomes: list.map((x) => x.id), roles: { ...roles },
            question: c.ask ?? `${block.name}: on whom?`, labels: list.map((x) => x.name),
        });
    }
    /** An `each` role a person fills creature by creature (`chooses.most`): at least one, then up to the most, stopping at No more. */
    chooseMany(block, r, roles) {
        const c = r.chooses;
        const f = this.frame(block, roles, {}, 0);
        this.frameNow = f;
        const limit = Math.max(1, Math.min(14, Math.floor(this.number(this.formula(f, c.most), 'how many creatures it may take'))));
        const self = block.roles.find((x) => x.pick === 'self')?.key;
        const of = self === undefined ? undefined : roles[self];
        const listed = of !== undefined && this.ctx.creatures ? this.ctx.creatures({ of, relation: c.relation }) ?? [] : [];
        if (!listed.length)
            throw new Stop(`${block.name}: there is no creature to choose`);
        const picked = [];
        while (picked.length < limit) {
            const left = listed.filter((x) => !picked.includes(x.id)).slice(0, MAX_CHOICES - 1);
            if (!left.length)
                break;
            const more = picked.length > 0;
            const answer = this.choose({
                who: 'actor', block: block.id, blockName: block.name, node: r.key, outcomes: [...left.map((x) => x.id), ...(more ? [NONE_CHOSEN] : [])], roles: { ...roles },
                question: `${c.ask ?? `${block.name}: on whom?`} (${picked.length + 1} of at most ${limit})`, labels: [...left.map((x) => x.name), ...(more ? ['No more'] : [])],
            });
            if (answer === NONE_CHOSEN)
                break;
            picked.push(answer);
        }
        return picked;
    }
    /** A number the context did not know (qa-runs 264): the next recorded answer, else the DM is asked for it. */
    answerNumber(f, what, creature, role) {
        if (this.used < this.state.choices.length) {
            const c = this.state.choices[this.used++];
            const n = chainNumber(c);
            if (n === null)
                throw new Stop(`the answer "${c}" is not a whole number`);
            return n;
        }
        throw new Pause({
            who: 'dm', block: f.block.id, blockName: f.block.name, node: '', outcomes: [], roles: { ...f.roles },
            question: `${role === undefined ? 'The attack' : this.roleLabel(f, role)}'s ${what}?`, value: { ...(creature !== undefined ? { creature } : {}), what },
        });
    }
    /** Carry on from where an End's `then` goes (part 3: the attack End, settled by `attack.ts`, followed by its outcome). */
    followFrom(top, roles, next, carry) {
        const f = this.frame(top, roles, {}, 0);
        if (typeof next === 'string') {
            this.runNode(f, this.node(f, next), carry);
            return;
        }
        const inner = this.resolve(next);
        this.runBlock(inner, this.mapRoles(f, inner, undefined), {}, carry, 1);
    }
    /** Count one more run of `key`; on the 21st repeat, stop (§5.3). */
    repeat(key, name) {
        const n = (this.counts.get(key) ?? 0) + 1;
        this.counts.set(key, n);
        if (n - 1 > CHAIN_REPEATS)
            throw new Stop(`${this.top.name} stopped after ${CHAIN_REPEATS} repeats of ${name}`);
    }
    // ── Blocks ───────────────────────────────────────────────────────────────
    resolve(ref) {
        const b = this.ctx.resolve(ref);
        if (!b)
            throw new Stop(`the block "${ref.block}" version ${ref.version} is not in the library`);
        return b;
    }
    frame(block, roles, inputs, depth) {
        if (depth > MAX_FRAMES)
            throw new Stop(`${block.name} is used more than ${MAX_FRAMES} blocks deep`);
        return { block, roles, inputs, memo: new Map(), chosen: new Map(), visiting: new Set(), depth };
    }
    finishes(n) {
        // An End that chooses (issue 1508) is a value, asked when something reads it, never a chain of its own.
        if (n.end)
            return n.end.choose === undefined;
        if (!n.use)
            return false;
        const kind = this.resolve(n.use).kind;
        return kind === 'mechanic' || kind === 'end' || kind === 'entry';
    }
    runBlock(block, roles, inputs, carry, depth) {
        if (block.kind === 'mechanic' || block.kind === 'end' || block.kind === 'entry')
            this.repeat(`${block.id}@${block.version}`, block.name);
        const f = this.frame(block, roles, inputs, depth);
        const targets = new Set();
        for (const n of block.nodes)
            for (const t of Object.values(n.end?.then ?? {}))
                if (typeof t === 'string')
                    targets.add(t);
        for (const n of block.nodes)
            if (!targets.has(n.id) && this.finishes(n))
                this.runNode(f, n, carry);
    }
    runNode(f, n, carry) {
        if (n.end) {
            this.runEnd(f, n, n.end, carry);
            return;
        }
        if (n.use && this.finishes(n)) {
            const inner = this.resolve(n.use);
            this.runBlock(inner, this.mapRoles(f, inner, n.roles), this.inputsOf(f, n), carry, f.depth + 1);
            return;
        }
        this.valueOf(f, n.id);
    }
    /** The inner block's roles filled from this frame's: by `map`, else by the same key. Unfilled roles stay out. */
    mapRoles(f, inner, map) {
        const out = {};
        for (const r of inner.roles) {
            const creature = f.roles[map?.[r.key] ?? r.key];
            if (creature !== undefined)
                out[r.key] = creature;
        }
        return out;
    }
    inputsOf(f, n) {
        const out = {};
        for (const [k, piece] of Object.entries(n.inputs ?? {}))
            out[k] = this.number(this.formula(f, piece), `${f.block.name}'s input ${k}`);
        return out;
    }
    // ── Values ───────────────────────────────────────────────────────────────
    node(f, id) {
        const n = f.block.nodes.find((x) => x.id === id);
        if (!n)
            throw new Stop(`${f.block.name} has no node "${id}"`);
        return n;
    }
    valueOf(f, id) {
        const known = f.memo.get(id);
        if (known !== undefined)
            return known;
        if (f.visiting.has(id))
            throw new Stop(`${f.block.name}'s node ${id} reads itself`);
        f.visiting.add(id);
        const n = this.node(f, id);
        let v;
        if (n.formula !== undefined)
            v = this.formula(f, n.formula);
        else if (n.direct)
            v = this.direct(f, n.direct);
        else if (n.end?.choose?.number) {
            const c = this.chooseEnd(f, n);
            v = c === null ? 0 : Number(c);
        }
        else if (n.end?.choose)
            throw new Stop(`${f.block.name}'s node ${id} chooses a creature, which is not a number`);
        else if (n.use && !this.finishes(n)) {
            const inner = this.resolve(n.use);
            const g = this.frame(inner, this.mapRoles(f, inner, n.roles), this.inputsOf(f, n), f.depth + 1);
            v = this.valueOf(g, valueNode(inner));
        }
        else
            throw new Stop(`${f.block.name}'s node ${id} is an outcome, not a value`);
        f.visiting.delete(id);
        f.memo.set(id, v);
        return v;
    }
    direct(f, d) {
        switch (d.source) {
            case 'fixed':
                if (d.input !== undefined && f.inputs[d.input] !== undefined)
                    return f.inputs[d.input];
                return d.value ?? 0;
            case 'attack': {
                const v = this.ctx.direct({ source: 'attack', key: d.key ?? '' });
                if (v === null || v === undefined)
                    throw new Unknown(`the attack's ${(d.key ?? '').replace(/_/g, ' ')} is not known`);
                return v;
            }
            case 'dice': {
                const count = this.number(this.formula(f, d.count), 'a dice count');
                const sides = this.number(this.formula(f, d.sides), 'a dice size');
                if (!Number.isInteger(count) || count < 0 || !Number.isInteger(sides) || sides < 1)
                    throw new Stop(`${count}d${sides} is not a roll`);
                return { dice: count ? [{ count, sides }] : [], bonus: 0 };
            }
            default: {
                const creature = this.creature(f, d.role);
                const to = d.to === undefined ? undefined : this.creature(f, d.to);
                const ask = d.source === 'sheet' ? { source: 'sheet', creature, purpose: d.purpose ?? '', ...(d.which !== undefined ? { which: d.which } : {}), ...(Array.isArray(d.against) ? { against: d.against.filter((t) => typeof t === 'string') } : {}) }
                    : d.source === 'stat' ? { source: 'stat', creature, register: d.register ?? '' }
                        : d.source === 'defence' ? { source: 'defence', creature, key: d.key ?? '' }
                            : d.source === 'condition' ? { source: 'condition', creature, key: d.key ?? '' }
                                : { source: 'board', creature, measure: d.measure ?? '', ...(to !== undefined ? { to } : {}) };
                const v = this.ctx.direct(ask);
                if (v === null || v === undefined) {
                    if (this.ctx.askUnknown && d.source !== 'condition' && !(d.source === 'board' && d.measure === 'adjacent'))
                        return this.answerNumber(f, valueWords(d), creature, d.role);
                    const what = d.source === 'sheet' ? `${d.purpose}${d.which ? ` (${d.which})` : ''}` : d.source === 'stat' ? d.register : d.source === 'defence' || d.source === 'condition' ? d.key : d.measure;
                    throw new Unknown(`${this.roleLabel(f, d.role)}'s ${what} is not known`);
                }
                return v;
            }
        }
    }
    roleLabel(f, key) {
        return f.block.roles.find((r) => r.key === key)?.label ?? key ?? 'a role';
    }
    creature(f, role) {
        const c = role === undefined ? undefined : f.roles[role];
        if (c === undefined)
            throw new Stop(`${f.block.name}: no creature fills ${this.roleLabel(f, role)}`);
        return c;
    }
    /**
     * A formula piece worked out by the sheet engine, every `in` and Direct written in as its literal. A piece that is a
     * number as it stands (a literal, a fixed value, an input passed in: a grip's 1½, issue 1502) is that number; only an
     * operation's result is rounded (§3.4: "a formula that divides is rounded").
     */
    formula(f, piece) {
        const tree = this.literal(f, piece);
        if (typeof tree === 'number' || typeof tree === 'boolean')
            return tree;
        const r = evaluate(tree);
        if (r.status !== 'computed')
            throw new Stop(`${f.block.name}: a formula could not be worked out (${r.reason ?? r.status})`);
        if (typeof r.value === 'number' || typeof r.value === 'boolean')
            return this.rounded(r.value);
        if (isDice(r.value))
            return { dice: r.value.dice.map((p) => ({ count: p.count, sides: p.sides })), bonus: r.value.bonus };
        throw new Stop(`${f.block.name}: a formula gave ${JSON.stringify(r.value)}, which is not a number, a yes or no, or dice`);
    }
    rounded(v) {
        if (typeof v !== 'number' || Number.isInteger(v) || !this.ctx.rounding)
            return v;
        const m = this.ctx.rounding;
        return (m === 'down' ? Math.floor(v) : m === 'up' ? Math.ceil(v) : Math.sign(v) * Math.round(Math.abs(v)));
    }
    literal(f, v) {
        if (Array.isArray(v))
            return v.map((x) => this.literal(f, x));
        if (!isObj(v))
            return v;
        const keys = Object.keys(v);
        if (keys.length === 1 && keys[0] === 'in' && typeof v.in === 'string')
            return asLiteral(this.valueOf(f, v.in));
        if (keys.length === 1 && keys[0] === 'direct' && isObj(v.direct))
            return asLiteral(this.direct(f, v.direct));
        const out = {};
        for (const k of keys)
            out[k] = this.literal(f, v[k]);
        return out;
    }
    number(v, what) {
        if (typeof v !== 'number')
            throw new Stop(`${what} is ${isDice(v) ? 'dice' : 'a yes or no'}, not a number`);
        return v;
    }
    // ── Ends ─────────────────────────────────────────────────────────────────
    /** A value thrown: dice give their faces' sum as the natural result, a number is its own total. */
    throwValue(v, multiplier = 1) {
        if (typeof v === 'number')
            return { natural: null, total: v, faces: [], bonus: v };
        if (typeof v === 'boolean')
            throw new Stop('a yes or no cannot be thrown');
        const faces = [];
        for (const p of v.dice)
            for (let i = 0; i < p.count * multiplier; i++)
                faces.push(this.draw(p.sides));
        const natural = faces.reduce((a, b) => a + b, 0);
        const bonus = v.bonus * multiplier;
        return { natural, total: natural + bonus, faces, bonus };
    }
    runEnd(f, n, e, carry) {
        this.frameNow = f;
        this.repeat(`${f.block.id}@${f.block.version}#${n.id}`, f.block.name);
        let outcome = null;
        let judged;
        if (e.branches.length) {
            if (e.resolved_by === 'rules')
                ({ outcome, judged } = this.judge(f, e, carry));
            else
                outcome = this.choose({ who: e.resolved_by, block: f.block.id, blockName: f.block.name, node: n.id, outcomes: [...e.branches], roles: { ...f.roles }, ...(e.ask ? { question: e.ask } : {}), ...(e.labels ? { labels: e.branches.map((x) => e.labels[x] ?? x), branch: true } : {}) });
        }
        else if (e.judge === 'damage')
            judged = this.judgeDamage(f, e, carry);
        let resolves;
        if (e.resolves) {
            const r = e.resolves;
            const onto = this.creature(f, r.onto);
            if (r.kind === 'damage') {
                const amount = e.judge === 'damage' && judged ? judged.total : this.throwValue(this.formula(f, r.value)).total;
                resolves = { kind: 'damage', onto, amount, ...(e.judge === 'damage' && judged?.damageKind ? { damageKind: judged.damageKind } : {}), ...(r.type ? { type: r.type } : {}) };
            }
            else if (r.kind === 'sheet') {
                resolves = { kind: 'sheet', onto, amount: this.number(this.formula(f, r.value), 'the value it writes'), ...(r.purpose ? { purpose: r.purpose } : {}) };
            }
            else if (r.kind === 'term') {
                const said = r.with;
                resolves = { kind: 'term', onto, to: r.to === 'damage' ? 'damage' : 'attack', amount: r.value === undefined && said ? 0 : this.number(this.formula(f, r.value), "the term's value"), ...(isAdvantageWord(said) ? { with: said } : {}) };
            }
            else if (r.kind === 'effect' && r.mods) {
                const x = r.extra_attacks;
                const foes = r.foes && this.ctx.creatures ? this.ctx.creatures({ of: onto, relation: 'enemy', within: r.foes.within }) ?? [] : undefined;
                resolves = { kind: 'effect', onto, lasts: r.lasts ?? 'next_turn', mods: this.mods(f, r.mods), ...(r.allies ? { allies: true } : {}), ...(x ? { extra: { count: x.count, ...(x.only ? { only: x.only } : {}) } } : {}), ...(r.conceal ? { conceal: r.conceal } : {}), ...(r.images ? { images: Math.max(1, Math.min(r.images.most, this.throwValue(this.formula(f, r.images.count)).total)) } : {}), ...(r.ward ? { ward: { amount: r.ward.amount, bypass: [...r.ward.bypass], total: Math.floor(this.number(this.formula(f, r.ward.total), 'what it stops in all')) } } : {}), ...(r.rounds !== undefined ? { rounds: this.roundsOf(r.rounds) } : {}), ...(r.label ? { label: r.label } : {}), ...(r.ends_on ? { endsOn: r.ends_on } : {}), ...(r.foes ? { foes: foes ?? [] } : {}), ...(r.temp_hp !== undefined ? { temp: Math.max(0, Math.min(1000, this.throwValue(this.formula(f, r.temp_hp)).total)) } : {}), ...(r.keen === true ? { keen: true } : {}), ...(typeof r.grow === 'number' && Number.isInteger(r.grow) && r.grow !== 0 && Math.abs(r.grow) <= 4 ? { grow: r.grow } : {}) };
            }
            else if (r.kind === 'report') {
                const shown = this.formula(f, r.value);
                const amount = isDice(shown) ? this.throwValue(shown).total : this.number(shown, 'the number it reports');
                // Thrown once: a later End reads this number by `in` (the turning check's total, in the Hit Dice it turns).
                f.memo.set(n.id, amount);
                resolves = { kind: 'report', onto, amount, ...(r.label ? { label: r.label } : {}) };
            }
            else if (r.kind === 'ability') {
                const shown = this.formula(f, r.value);
                const amount = Math.max(0, isDice(shown) ? this.throwValue(shown).total : this.number(shown, 'the points it costs'));
                resolves = { kind: 'ability', onto, amount, ...(r.key ? { key: r.key } : {}), ...(r.how ? { how: r.how } : {}), ...(r.label ? { label: r.label } : {}), ...(this.ctx.isCharacter?.(onto) ? { confirm: true } : {}) };
            }
            else if (r.kind === 'burst') {
                const v = this.formula(f, r.value);
                const damage = isDice(v) ? { parts: v.dice.map((d) => ({ count: d.count, sides: d.sides })), bonus: v.bonus } : { parts: [], bonus: this.number(v, 'what a burst deals') };
                const s = r.save;
                resolves = {
                    kind: 'burst', onto, ...(r.label ? { label: r.label } : {}),
                    burst: { within: r.within, affects: r.affects, ...(r.heals ? { heals: true } : {}), damage, save: s.key, dc: Math.floor(this.number(this.formula(f, s.dc), "the save's DC")), onSave: s.on_save },
                };
            }
            else if (r.kind === 'heal') {
                // The creature touched, by the End that chose it: none chosen, nothing happens.
                const on = r.target ? this.chooseEnd(f, this.node(f, r.target.in)) : onto;
                if (on) {
                    const kind = this.ctx.kindOf ? this.ctx.kindOf(on) : null;
                    const shown = this.formula(f, r.value);
                    let amount = this.throwValue(shown).total;
                    const harmed = r.harms !== undefined && kind === r.harms;
                    const spends = r.spends ? { spends: true } : {};
                    if (harmed && r.halved) {
                        const save = this.judge(f, { branches: ['success', 'failure'], resolved_by: 'rules', judge: 'save', value: r.halved.value, against: r.halved.against }, carry);
                        judged = save.judged;
                        if (save.outcome === 'success')
                            amount = Math.floor(amount / 2);
                    }
                    if (r.passes === undefined || kind !== r.passes)
                        resolves = harmed ? { kind: 'damage', onto: on, amount, ...spends } : { kind: 'heal', onto: on, amount, ...spends, ...(r.harms !== undefined && kind === null ? { asks: true } : {}) };
                }
            }
            else if (r.kind === 'attack') {
                // The attack that set the entry off, again (issue 1509): against nobody chosen, none.
                const against = typeof r.against === 'string' ? this.creature(f, r.against) : r.against ? this.chooseEnd(f, this.node(f, r.against.in)) : null;
                if (against)
                    resolves = { kind: 'attack', onto, against, ...(r.mods ? { lasts: r.lasts ?? 'next_turn', mods: this.mods(f, r.mods) } : {}) };
            }
            else if (r.kind === 'condition') {
                const rounds = r.rounds === undefined ? undefined : this.roundsOf(r.rounds);
                const foes = r.turn ? this.turnFoes(f, onto, r.turn) : r.foes && this.ctx.creatures ? this.ctx.creatures({ of: onto, relation: 'enemy', within: r.foes.within }) ?? [] : undefined;
                const retry = r.retry ? { save: r.retry.save, dc: this.number(this.formula(f, r.retry.dc), "the repeated save's DC") } : undefined;
                resolves = { kind: r.kind, onto, ...(r.key ? { key: r.key } : {}), ...(rounds !== undefined ? { rounds } : {}), ...(retry ? { retry } : {}), ...(r.foes || r.turn ? { foes: foes ?? [] } : {}), ...(r.confirm === true || (r.confirm === 'unknown' && !this.ctx.kindKnown?.(onto)) ? { confirm: true } : {}), ...(r.turn ? { turn: true } : {}) };
            }
            else
                resolves = { kind: r.kind, onto, ...(r.key ? { key: r.key } : {}), ...(r.label ? { label: r.label } : {}) };
        }
        // A kind is a fork in the rule, not a thing that happened: it says nothing, its `note` does.
        if (e.judge !== 'kind')
            this.ends.push({
                block: f.block.id, version: f.block.version, blockName: f.block.name, node: n.id, roles: { ...f.roles }, outcome, resolvedBy: e.resolved_by,
                ...(judged ? { judged } : {}), ...(resolves ? { resolves } : {}),
            });
        const next = outcome === null ? undefined : e.then?.[outcome];
        if (next === undefined)
            return;
        const on = judged?.critical && judged.multiplier ? { critical: judged.multiplier } : carry;
        if (typeof next === 'string')
            this.runNode(f, this.node(f, next), on);
        else {
            const inner = this.resolve(next);
            this.runBlock(inner, this.mapRoles(f, inner, undefined), {}, on, f.depth + 1);
        }
    }
    /** How long something lasts: a formula, or dice thrown; at least one round. */
    roundsOf(piece) {
        const lasts = this.formula(this.frameNow, piece);
        return Math.max(1, Math.floor(isDice(lasts) ? this.throwValue(lasts).total : this.number(lasts, 'how long it lasts')));
    }
    /**
     * Whom a turning reaches (feats.md §10c): nearest first, each of the kind it affects no stronger than `most` Hit Dice,
     * until the `total` Hit Dice run out; one already carrying the condition is passed over and costs nothing, and one at
     * `destroy_at` Hit Dice or fewer is destroyed. A creature of no known kind, or known but with no known Hit Dice, uses
     * none of the total: it is listed last for the DM, under its name and those words.
     */
    turnFoes(f, of, t) {
        const most = this.number(this.formula(f, t.most), 'the most Hit Dice turned');
        let left = this.number(this.formula(f, t.total), 'the total Hit Dice turned');
        const destroyAt = t.destroy_at === undefined ? 0 : this.number(this.formula(f, t.destroy_at), 'the Hit Dice destroyed');
        const reached = this.ctx.reached ? this.ctx.reached({ of, within: t.within }) ?? [] : [];
        const key = f.block.nodes.flatMap((n) => (n.end?.resolves?.turn ? [n.end.resolves.key ?? ''] : []))[0] ?? '';
        const sure = [];
        const unsure = [];
        for (const c of reached) {
            if (c.id === of || c.carries.includes(key) || (c.kind !== null && c.kind !== t.affects))
                continue;
            if (c.kind === null || c.hd === null) {
                unsure.push({ id: c.id, name: `${c.name} (kind or Hit Dice not known)` });
                continue;
            }
            if (c.hd > most || c.hd > left)
                continue;
            left -= c.hd;
            sure.push({ id: c.id, name: c.name, ...(c.hd <= destroyAt ? { destroys: true } : {}) });
        }
        return [...sure, ...unsure];
    }
    /**
     * A table effect's numbers worked out (issue 1508): each a whole number (a fraction rounded as the system rounds), a
     * number worth nothing left out, and one against a creature nobody chose left out too.
     */
    mods(f, list) {
        const out = [];
        for (const m of list) {
            let vs;
            if (typeof m.vs === 'string')
                vs = this.creature(f, m.vs);
            else if (m.vs && typeof m.vs.in === 'string')
                vs = this.chooseEnd(f, this.node(f, m.vs.in));
            if (vs === null)
                continue;
            const raw = this.number(this.formula(f, m.value), "an effect's number");
            const value = Number.isInteger(raw) ? raw : Math.floor(raw);
            if (value === 0)
                continue;
            out.push({ target: m.target, value, ...(m.type ? { type: m.type } : {}), ...(m.only ? { only: m.only } : {}), ...(vs !== undefined ? { vs } : {}), ...(m.once ? { once: true } : {}), ...(m.which ? { which: m.which } : {}), ...(m.against?.length ? { against: [...m.against] } : {}) });
        }
        return out;
    }
    /**
     * An End that chooses (issue 1508; feats.md §6.1), asked once per run of its block: a whole number from its min to
     * its max (at most MAX_CHOICES buttons), or one of a creature's enemies the context lists. The answer is kept, and the
     * End is reported with it as its outcome. No enemy to choose is no answer (null), and nobody is asked.
     */
    chooseEnd(f, n) {
        if (f.chosen.has(n.id))
            return f.chosen.get(n.id);
        const e = n.end;
        const c = e.choose;
        this.repeat(`${f.block.id}@${f.block.version}#${n.id}`, f.block.name);
        let outcomes;
        let labels;
        if (c.number) {
            const lo = Math.ceil(this.number(this.formula(f, c.number.min), 'the least it may choose'));
            const hi = Math.max(lo, Math.min(lo + MAX_CHOICES - 1, Math.floor(this.number(this.formula(f, c.number.max), 'the most it may choose'))));
            outcomes = Array.from({ length: hi - lo + 1 }, (_, i) => String(lo + i));
        }
        else if (c.creature) {
            const of = this.creature(f, c.creature.of);
            const next = c.creature.adjacent_to === undefined ? undefined : this.creature(f, c.creature.adjacent_to);
            const listed = this.ctx.creatures ? this.ctx.creatures({ of, relation: c.creature.relation, ...(next !== undefined ? { adjacentTo: next } : {}) }) : [];
            if (listed === null)
                throw new Unknown(`${this.roleLabel(f, c.creature.of)}'s enemies are not known`);
            outcomes = listed.slice(0, MAX_CHOICES).map((x) => x.id);
            labels = listed.slice(0, MAX_CHOICES).map((x) => x.name);
            // Declining (issue 1509): a No button after the creatures, when there is one to choose.
            if (c.creature.optional && outcomes.length) {
                outcomes.push(NONE_CHOSEN);
                labels.push('No');
            }
        }
        else
            throw new Stop(`${f.block.name}'s node ${n.id} chooses nothing it knows`);
        let answer = null;
        if (outcomes.length === 1 && c.number)
            answer = outcomes[0];
        else if (outcomes.length) {
            answer = this.choose({
                who: e.resolved_by === 'dm' ? 'dm' : 'actor', block: f.block.id, blockName: f.block.name, node: n.id, outcomes, roles: { ...f.roles },
                ...(e.ask ? { question: e.ask } : {}), ...(labels ? { labels } : {}),
            });
        }
        const chosen = answer === NONE_CHOSEN && c.creature?.optional ? null : answer;
        f.chosen.set(n.id, chosen);
        this.ends.push({ block: f.block.id, version: f.block.version, blockName: f.block.name, node: n.id, roles: { ...f.roles }, outcome: answer, resolvedBy: e.resolved_by });
        return chosen;
    }
    judge(f, e, carry) {
        const pick = (o) => {
            if (!e.branches.includes(o))
                throw new Stop(`${f.block.name}: the rules gave "${o}", which is not one of its branches`);
            return o;
        };
        switch (e.judge) {
            case 'attack': {
                const a = this.ctx.combat?.attack;
                if (!a)
                    throw new Stop("this system's combat rules have no attack");
                const value = this.formula(f, e.value);
                const against = this.number(this.formula(f, e.against), 'what an attack is against');
                const t = this.throwValue(value);
                const versus = (nat, tot) => {
                    const named = nat === null ? undefined : a.natural.find((x) => x.face === nat);
                    return named ? named.result === 'hit' : tot >= against;
                };
                const base = { natural: t.natural, total: t.total, against, faces: t.faces };
                if (!versus(t.natural, t.total))
                    return { outcome: pick('miss'), judged: { ...base, threat: false } };
                const threatAt = this.ctx.critical?.threat ?? a.critical?.threat;
                const multiplier = this.ctx.critical?.multiplier ?? a.critical?.multiplier ?? 1;
                const threat = a.critical !== null && threatAt !== undefined && t.natural !== null && t.natural >= threatAt;
                if (!threat)
                    return { outcome: pick('hit'), judged: { ...base, threat: false } };
                let critical = true;
                let confirmation;
                if (a.critical.confirm === 'roll_again') {
                    const c = this.throwValue(value);
                    critical = versus(c.natural, c.total);
                    confirmation = { natural: c.natural ?? c.total, total: c.total, confirmed: critical };
                }
                const judged = { ...base, threat: true, ...(confirmation ? { confirmation } : {}), critical, multiplier };
                const outcome = critical && e.branches.includes('critical') ? 'critical' : e.branches.includes('threat') ? 'threat' : 'hit';
                return { outcome: pick(outcome), judged };
            }
            case 'kind': {
                const role = e.of ?? 'target';
                const id = this.creature(f, role);
                const word = e.is ?? '';
                const known = this.ctx.isKind ? this.ctx.isKind(id, word) : null;
                if (known !== null)
                    return { outcome: pick(known ? e.branches[0] : e.branches[1]) };
                const name = this.ctx.nameOf?.(id) ?? this.roleLabel(f, role).toLowerCase();
                return { outcome: pick(this.choose({ who: 'dm', block: f.block.id, blockName: f.block.name, node: '', outcomes: [e.branches[0], e.branches[1]], roles: { ...f.roles }, question: `${f.block.name}: is ${name} ${word.replace(/_/g, ' ')}?` })) };
            }
            case 'save': {
                const s = this.ctx.combat?.saves;
                if (!s)
                    throw new Stop("this system's combat rules have no saves");
                const against = this.number(this.formula(f, e.against), 'the difficulty class');
                const value = this.formula(f, e.value);
                const at = { faces: this.faces.length, answers: this.used };
                const t = this.throwValue(value);
                const named = t.natural === null ? undefined : s.natural.find((x) => x.face === t.natural);
                const ok = named ? named.result === 'success' : t.total >= against;
                return { outcome: pick(ok ? 'success' : 'failure'), judged: { natural: t.natural, total: t.total, against, faces: t.faces, at } };
            }
            case 'compare': {
                const against = this.number(this.formula(f, e.against), 'what it is compared with');
                const t = this.throwValue(this.formula(f, e.value));
                const o = t.total >= against ? e.branches[0] : e.branches[1];
                if (o === undefined)
                    throw new Stop(`${f.block.name}: a comparison needs two branches`);
                return { outcome: o, judged: { natural: t.natural, total: t.total, against, faces: t.faces } };
            }
            default:
                throw new Stop(`${f.block.name}: the rules pick a branch by a judge, and this End has ${e.judge ? `"${e.judge}"` : 'none'}`);
        }
    }
    /** Damage (judge `damage`): thrown, multiplied after a critical on the chain, never under the system's least. */
    judgeDamage(f, e, carry) {
        const piece = e.value ?? e.resolves?.value;
        if (piece === undefined)
            throw new Stop(`${f.block.name}: the damage End names no damage`);
        const value = this.formula(f, piece);
        const multiplier = carry.critical ?? 1;
        // Each pool's own faces first, then a critical's extra rolls of it, as attack.ts throws a part's faces and its crit.
        let faces = [];
        let bonus;
        if (typeof value === 'number')
            bonus = value * multiplier;
        else if (isDice(value)) {
            for (const p of value.dice) {
                for (let i = 0; i < p.count; i++)
                    faces.push(this.draw(p.sides));
                for (let i = 0; i < p.count * (multiplier - 1); i++)
                    faces.push(this.draw(p.sides));
            }
            bonus = value.bonus * multiplier;
        }
        else
            throw new Stop('a yes or no is not damage');
        // The extra dice (issue 1502), after the value's own and its critical's: thrown once, whatever the multiplier.
        if (e.extra !== undefined) {
            const x = this.formula(f, e.extra);
            if (typeof x === 'boolean')
                throw new Stop('a yes or no is not damage');
            const t = this.throwValue(x);
            faces = [...faces, ...t.faces];
            bonus += typeof x === 'number' ? x : x.bonus;
        }
        const rolled = faces.reduce((a, b) => a + b, 0);
        const least = leastDamage(rolled + bonus, this.ctx.combat?.minimumDamage, this.ctx.combat?.minimumDamageKind);
        return { natural: faces.length ? rolled : null, total: least.total, against: null, faces, ...(multiplier > 1 ? { critical: true, multiplier } : {}), ...(least.kind ? { damageKind: least.kind } : {}) };
    }
}
/** A value as a literal the sheet engine reads: dice as a `sum` of `dice` pieces and the bonus. */
function asLiteral(v) {
    if (!isDice(v))
        return v;
    const parts = v.dice.map((p) => ({ dice: { count: p.count, sides: p.sides } }));
    return parts.length ? { sum: [...parts, v.bonus] } : v.bonus;
}
/** A primitive's (or a Direct block's) value node: the one node nothing else reads. */
function valueNode(block) {
    const read = new Set();
    const walk = (v) => {
        if (Array.isArray(v)) {
            v.forEach(walk);
            return;
        }
        if (!isObj(v))
            return;
        if (typeof v.in === 'string' && Object.keys(v).length === 1) {
            read.add(v.in);
            return;
        }
        Object.values(v).forEach(walk);
    };
    for (const n of block.nodes) {
        walk(n.formula);
        walk(n.direct);
        walk(n.inputs);
        walk(n.end);
    }
    const values = block.nodes.filter((n) => !read.has(n.id));
    if (values.length !== 1)
        throw new Stop(`${block.name} gives ${values.length ? 'more than one value' : 'no value'}`);
    return values[0].id;
}
/** Every combination of the `each` roles' creatures (one run each, §4.3's Fireball). */
function combinations(lists) {
    let out = [{}];
    for (const [key, ids] of lists)
        out = out.flatMap((c) => ids.map((id) => ({ ...c, [key]: id })));
    return out;
}
/**
 * Run a block's chains (§7): from the start, or — with `state` — again from the start with the faces and answers
 * recorded so far, carrying on past where it paused. Returns the Ends reached, and either `done`, `paused` with who is
 * asked, or `stopped` with why.
 */
export function runChain(block, input, ctx, state = { faces: [], choices: [] }) {
    const run = new Run(block, ctx, state);
    const out = () => ({ faces: [...run.faces], choices: [...run.state.choices], ...(run.state.rest ? { rest: run.state.rest } : {}) });
    try {
        const base = {};
        const each = [];
        for (const r of block.roles) {
            const v = input.roles[r.key];
            if (v === undefined) {
                if (r.chooses?.most !== undefined && r.pick === 'each')
                    each.push([r.key, run.chooseMany(block, r, base)]);
                else if (r.chooses)
                    base[r.key] = run.chooseRole(block, r, base);
                continue;
            }
            const ids = Array.isArray(v) ? v : [v];
            if (r.pick === 'each')
                each.push([r.key, ids]);
            else if (ids.length === 1)
                base[r.key] = ids[0];
            else
                throw new Stop(`${r.label} is one creature, and ${ids.length} were given`);
        }
        for (const [i, combo] of combinations(each).entries()) {
            run.counts = new Map();
            const firstEnd = run.ends.length;
            run.runBlock(block, { ...base, ...combo }, { ...(input.inputs ?? {}) }, {}, 0);
            run.finish(i, firstEnd);
        }
        return { status: 'done', ends: run.ends, state: out() };
    }
    catch (x) {
        if (x instanceof Pause)
            return { status: 'paused', ask: x.ask, ends: run.ends, state: out() };
        if (x instanceof Stop)
            return { status: 'stopped', reason: x.reason, ends: run.ends, state: out() };
        throw x;
    }
}
const signedText = (n) => (n < 0 ? `\u2212${-n}` : `+${n}`);
/** A table effect's numbers in words (issue 1508): "−3 attack, +3 damage until its next turn"; none is "not used". */
export function modsText(mods, lasts, rounds) {
    if (!mods.length)
        return 'not used';
    const what = (m) => `${signedText(m.value)} ${m.target === 'ac' ? `${m.type ? `${m.type.replace(/_/g, ' ')} ` : ''}AC` : modTargetWords(m.target)}${m.only ? ` (${m.only})` : ''}`;
    return `${mods.map(what).join(', ')}${lasts !== 'encounter' ? ' until its next turn' : rounds ? ` for ${rounds} round${rounds === 1 ? '' : 's'}` : ''}`;
}
/** Carry on a paused run with the asked person's choice. Throws on a choice that is not one of the outcomes asked. */
export function resumeChain(block, input, ctx, paused, choice) {
    // A number asked (qa-runs 264) takes a whole number; an End's question, one of its outcomes.
    if (paused.ask.value ? chainNumber(choice) === null : !paused.ask.outcomes.includes(choice))
        throw new RangeError(paused.ask.value ? `"${choice}" is not a whole number` : `"${choice}" is not one of ${paused.ask.outcomes.join(', ')}`);
    return runChain(block, input, ctx, { faces: [...paused.state.faces], choices: [...paused.state.choices, choice] });
}
/** A deterministic die from a seed (mulberry32): for tests and replays, never for the table's own throws. */
export function seededDie(seed) {
    let a = seed >>> 0;
    return (sides) => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        const r = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        return 1 + Math.floor(r * sides);
    };
}
const sameTree = (a, b) => JSON.stringify(a) === JSON.stringify(b);
/** A piece with every `in` written out as the node it reads (its formula, its Direct, or its `use`). */
function expanded(block, piece, depth = 0) {
    if (depth > 32)
        return piece;
    if (Array.isArray(piece))
        return piece.map((x) => expanded(block, x, depth + 1));
    if (!isObj(piece))
        return piece;
    const keys = Object.keys(piece);
    if (keys.length === 1 && keys[0] === 'in' && typeof piece.in === 'string') {
        const n = block.nodes.find((x) => x.id === piece.in);
        if (!n)
            return piece;
        if (n.formula !== undefined)
            return expanded(block, n.formula, depth + 1);
        if (n.direct)
            return { direct: n.direct };
        if (n.use)
            return { use: n.use, roles: n.roles ?? null, inputs: expanded(block, n.inputs ?? null, depth + 1) };
        return piece;
    }
    const out = {};
    for (const k of keys)
        out[k] = expanded(block, piece[k], depth + 1);
    return out;
}
/** The attack End of a block: the first End judged as an attack. */
function attackEnd(block) {
    return block.nodes.find((n) => n.end?.judge === 'attack')?.end ?? null;
}
/** The operands of the sum a value is (through its `in`s); a value that is no sum is its own one operand. */
function operands(block, piece) {
    let p = piece;
    for (let i = 0; i < 32 && isObj(p) && typeof p.in === 'string' && Object.keys(p).length === 1; i++) {
        const n = block.nodes.find((x) => x.id === p.in);
        if (!n || n.formula === undefined)
            break;
        p = n.formula;
    }
    return isObj(p) && Array.isArray(p.sum) && Object.keys(p).length === 1 ? p.sum : [piece];
}
/** What a piece is called on the attack's line: the block it uses, a fixed value's label, else the override's name. */
function labelOf(block, piece, ctx) {
    if (isObj(piece) && typeof piece.in === 'string' && Object.keys(piece).length === 1) {
        const n = block.nodes.find((x) => x.id === piece.in);
        if (n?.use)
            return ctx.resolve(n.use)?.name ?? block.name;
        if (n?.direct?.source === 'fixed' && n.direct.label)
            return n.direct.label;
        // A formula that uses exactly one block is that block's piece ("Raging (house rule)" wrapped in a cap).
        if (n?.formula !== undefined) {
            const used = new Set();
            const walk = (v) => {
                if (Array.isArray(v)) {
                    v.forEach(walk);
                    return;
                }
                if (!isObj(v))
                    return;
                if (typeof v.in === 'string' && Object.keys(v).length === 1) {
                    const m = block.nodes.find((x) => x.id === v.in);
                    if (m?.use)
                        used.add(ctx.resolve(m.use)?.name ?? block.name);
                    else if (m?.formula !== undefined)
                        walk(m.formula);
                    return;
                }
                Object.values(v).forEach(walk);
            };
            walk(n.formula);
            if (used.size === 1)
                return [...used][0];
        }
    }
    if (isObj(piece) && isObj(piece.direct) && piece.direct.source === 'fixed' && typeof piece.direct.label === 'string')
        return piece.direct.label;
    return block.name;
}
/**
 * The lines a campaign's override of the System Attack roll adds to an attack (see above), worked out with the table's
 * context for the roles given. Dice a piece adds are thrown with the context's `rng`. A piece whose value is not known
 * (a creature with no such number) is left out and named in `notes` for the DM; a piece worth 0 adds no line.
 */
export function houseTerms(override, system, input, ctx) {
    const terms = [];
    const notes = [];
    const unknown = [];
    const mine = attackEnd(override);
    const theirs = attackEnd(system);
    if (!mine || !theirs)
        return { terms, notes: mine ? notes : [`${override.name} has no attack to play, so the attack is played as the system's.`], unknown };
    const base = operands(system, theirs.value).map((p) => expanded(system, p));
    const left = [...base];
    const added = [];
    for (const p of operands(override, mine.value)) {
        const i = left.findIndex((q) => sameTree(q, expanded(override, p)));
        if (i >= 0)
            left.splice(i, 1);
        else
            added.push(p);
    }
    if (left.length)
        notes.push(`${override.name} leaves out part of the system's attack; the table still plays that part.`);
    if (!sameTree(expanded(override, mine.against), expanded(system, theirs.against)))
        notes.push(`${override.name} changes what the attack is against; the table still plays the system's.`);
    const roles = {};
    for (const r of override.roles) {
        const v = input.roles[r.key];
        if (typeof v === 'string')
            roles[r.key] = v;
    }
    for (const piece of added) {
        const reads = new Set();
        const watched = { ...ctx, direct: (a) => { if ('creature' in a)
                reads.add(a.creature); return ctx.direct(a); } };
        const run = new Run(override, watched, { faces: [], choices: [] });
        const label = labelOf(override, piece, ctx);
        try {
            const v = run.formula(run.frame(override, roles, { ...(input.inputs ?? {}) }, 0), piece);
            if (typeof v === 'boolean') {
                notes.push(`${label} gives a yes or no, not a number, so it was left out.`);
                continue;
            }
            const value = typeof v === 'number' ? v : run.throwValue(v).total;
            if (value !== 0)
                terms.push({ label, value, reads: [...reads] });
        }
        catch (x) {
            if (x instanceof Unknown) {
                unknown.push({ label, reads: [...reads], why: x.reason });
                continue;
            }
            if (x instanceof Stop) {
                notes.push(`${label} was left out: ${x.reason}.`);
                continue;
            }
            if (x instanceof Pause) {
                notes.push(`${label} was left out: it asks a person, which an attack's line cannot wait for yet.`);
                continue;
            }
            throw x;
        }
    }
    return { terms, notes, unknown };
}
/** The blocks a copy reaches through `use` and `then`, itself first; null when it has no attack End with a `then`. */
export function houseChainOf(override, library, roles, rounding) {
    const end = attackEnd(override);
    if (!end?.then || !Object.keys(end.then).length)
        return null;
    const blocks = [override];
    const seen = new Set([`${override.id}@${override.version}`]);
    for (let i = 0; i < blocks.length && blocks.length < 32; i++) {
        for (const n of blocks[i].nodes) {
            const refs = [...(n.use ? [n.use] : []), ...Object.values(n.end?.then ?? {}).filter((t) => typeof t !== 'string')];
            for (const r of refs) {
                const k = `${r.block}@${r.version}`;
                const b = library.find((x) => x.id === r.block && x.version === r.version);
                if (b && !seen.has(k)) {
                    seen.add(k);
                    blocks.push(b);
                }
            }
        }
    }
    return { blocks, roles, rounding, tape: [], faces: [], answers: [], ends: [] };
}
/** The attack End's branch for `attack.ts`'s outcome: a critical is `critical`, else `threat`, else `hit`; null while unresolved. */
export function attackBranch(chain, result, threat) {
    const branches = attackEnd(chain.blocks[0])?.branches ?? [];
    const pick = (...os) => os.find((o) => branches.includes(o)) ?? null;
    if (result === 'unresolved')
        return null;
    if (result === 'miss')
        return pick('miss');
    if (result === 'critical')
        return pick('critical', 'threat', 'hit');
    return threat ? pick('threat', 'hit') : pick('hit');
}
const resolvesText = (r) => {
    if (!r)
        return undefined;
    if (r.kind === 'damage')
        return `${r.amount}${r.damageKind ? ` ${r.damageKind}` : ''} damage`;
    if (r.kind === 'sheet')
        return `${r.amount} to ${r.purpose ?? 'the sheet'}`;
    if (r.kind === 'term')
        return `${signedText(r.amount ?? 0)} to ${r.to === 'damage' ? 'damage' : 'the attack'}`;
    if (r.kind === 'attack')
        return 'an attack made again';
    if (r.kind === 'heal')
        return `${r.amount} healed`;
    if (r.mods)
        return modsText(r.mods);
    return `${r.kind} ${r.key ?? ''}`.trim();
};
/**
 * Carry the chain on from the attack End's `branch` with the answers given so far (`chain.answers`), reading each Direct
 * from the tape first and from `live` after (whose answers join the tape). `critical` is the multiplier a critical
 * carries to a damage End. Returns the chain as it now stands: the Ends reached, and the person asked, or why it stopped.
 */
export function followAttack(chain, branch, ctx, critical) {
    const top = chain.blocks[0];
    const tape = new Map(chain.tape);
    const direct = (a) => {
        const k = JSON.stringify(a);
        if (tape.has(k))
            return tape.get(k);
        const v = ctx.direct(a);
        tape.set(k, v ?? null);
        return v ?? null;
    };
    const full = { ...ctx, direct, rounding: chain.rounding, resolve: (r) => chain.blocks.find((b) => b.id === r.block && b.version === r.version) ?? null };
    const out = { ...chain, branch, ends: [] };
    delete out.ask;
    delete out.stopped;
    const node = top.nodes.find((n) => n.end?.judge === 'attack');
    const next = node?.end?.then?.[branch];
    if (next === undefined)
        return { ...out, tape: [...tape], faces: [] };
    const run = new Run(top, full, { faces: [...chain.faces], choices: [...chain.answers] });
    try {
        run.followFrom(top, chain.roles, next, critical ? { critical } : {});
    }
    catch (x) {
        if (x instanceof Pause)
            out.ask = { who: x.ask.who, name: x.ask.blockName, outcomes: [...x.ask.outcomes] };
        else if (x instanceof Stop)
            out.stopped = x.reason;
        else
            throw x;
    }
    out.ends = run.ends.map((e) => ({ name: e.blockName, outcome: e.outcome, by: e.resolvedBy, ...(resolvesText(e.resolves) ? { resolves: resolvesText(e.resolves) } : {}) }));
    return { ...out, tape: [...tape], faces: [...run.faces] };
}
//# sourceMappingURL=chain.js.map